import {documentToFeature, documentToTask, getProjectPlan} from "@/app/data/converters";
import {Feature, ScopeStatus, Task, TimeEstimate} from "@/pb/api/devplan/types/tasks";
import {DateEstimate, Group, GroupItem, GroupItemTimeline, GroupTimeline} from "@/pb/api/devplan/types/grouping";
import {DocumentEntity, DocumentType} from "@/pb/api/devplan/types/documents";

export const UnassignedUserId = "--unassigned--"

export interface FeatureTimeline {
  featureId: string
  timeline: GroupItemTimeline
}

export class FeaturesAssigner {
  private readonly projectDocs: Record<string, DocumentEntity[]>
  private readonly allDocs: Record<string, DocumentEntity>
  private readonly features: Record<string, Feature>;
  private readonly featureEstimates: Record<string, TimeEstimate>;

  private projectsOrder: string[]

  // All fields below should be reset on recompute
  private userNextAvailability: Record<string, Date> = {}
  featureTimelinesByUser: Record<string, FeatureTimeline[]> = {};
  private featureTimelinesById: Record<string, GroupItemTimeline> = {}
  private assigned = false;

  public constructor(projectDocs: Record<string, DocumentEntity[]>, projectsOrder: string[]) {
    this.projectDocs = projectDocs
    this.allDocs = Object.values(projectDocs).flat()
      .reduce((a, d) => ({...a, [d.id]: d}), {} as Record<string, DocumentEntity>)
    this.features = Object.values(this.allDocs).filter(d => d.type === DocumentType.FEATURE).map(documentToFeature)
      .reduce((a, f) => ({...a, [f.id]: f}), {} as Record<string, Feature>)
    this.projectsOrder = projectsOrder
    this.featureEstimates = getFeatureEstimates(this.features, this.allDocs)
  }

  public getGroupTimeline(group: Group): GroupTimeline {
    this.prepare()
    const allItems = group.items.reduce((a, i) => ({...a, [i.key]: i}), {} as Record<string, GroupItem>)
    const itemTimelines = {...this.featureTimelinesById}
    this.fillTimelines(group.topLevelItems, allItems, itemTimelines)
    return GroupTimeline.create({itemTimelines})
  }

  private fillTimelines(
    itemKeys: string[],
    itemByKey: Record<string, GroupItem>,
    timelines: Record<string, GroupItemTimeline>,
  ): GroupItemTimeline | undefined {
    let minStart: Date | undefined = undefined
    let maxEnd: Date | undefined = undefined
    for (const itemKey of itemKeys) {
      const item = itemByKey[itemKey]
      if (!item) {
        continue
      }
      const timeline = timelines[itemKey] || this.fillTimelines(item.childItems, itemByKey, timelines)
      if (!timeline) {
        continue
      }
      timelines[itemKey] = timeline
      minStart = getMinDate(minStart, timeline.start?.ts)
      maxEnd = getMaxDate(maxEnd, timeline.end?.ts)
    }
    return (minStart && maxEnd) ? GroupItemTimeline.create({
      start: DateEstimate.create({ts: minStart}),
      end: DateEstimate.create({ts: maxEnd}),
    }) : undefined
  }

  private prepare() {
    if (this.assigned) {
      return
    }
    this.projectsOrder.forEach(projectId => {
      const docs = this.projectDocs[projectId] || []
      const allFeatures = docs.filter(d => d.type === DocumentType.FEATURE)
      const planIds = getProjectPlan(docs)?.details?.orderedFeatures
      const orderedFeatureIds = getOrderedFeatureIds(planIds, allFeatures)
      this.assignAndScheduleProjectFeatures(orderedFeatureIds)
    })
    this.assigned = true
  }

  private assignAndScheduleProjectFeatures(featureIds: string[]) {
    if (featureIds.length === 0) {
      return
    }
    const orderedAssignments: Record<string, string[]> = featureIds.reduce((a, id) => {
      const assignee = this.features[id]?.details?.assignee || UnassignedUserId
      return {...a, [assignee]: [...(a[assignee] || []), id]}
    }, {} as Record<string, string[]>)
    const {[UnassignedUserId]: unassignedIds, ...assigned} = orderedAssignments
    const newStarts = Object.entries(assigned).map(([userId, featureIds]) => this.scheduleForUser(userId, featureIds))
    if (unassignedIds && unassignedIds.length > 0) {
      this.scheduleForUser(UnassignedUserId, unassignedIds, getMaxDate(...newStarts))
    }
  }

  private scheduleForUser(userId: string, featureIds: string[], minStart?: Date) {
    const start = getMaxDate(minStart, this.userNextAvailability[userId]) || new Date
    const {items, newStart} = this.scheduleFeatures(start, featureIds)
    this.userNextAvailability[userId] = newStart
    this.featureTimelinesByUser[userId] = [...(this.featureTimelinesByUser[userId] || []), ...items]
    return newStart
  }

  private scheduleFeatures(startDate: Date, featureIds: string[]) {
    let newStart = startDate
    const timelines: FeatureTimeline[] = []
    for (const id of featureIds) {
      const estimate = this.featureEstimates[id]
      if (!estimate) {
        continue
      }
      const duration = (estimate.minHours + estimate.maxHours) / 2
      const end = new Date(newStart.getTime() + duration * 60 * 60 * 1000)
      const timeline = GroupItemTimeline.create({
        start: DateEstimate.create({ts: newStart}),
        end: DateEstimate.create({ts: end}),
      })
      this.featureTimelinesById[id] = timeline
      timelines.push({featureId: id, timeline})
      newStart = end
    }
    return {items: timelines, newStart}
  }
}

function getFeatureEstimates(features: Record<string, Feature>, allDocs: Record<string, DocumentEntity>) {
  const scopedFeatureIds = new Set(Object.entries(features)
    .filter(e => (e[1].details?.scopeStatus || ScopeStatus.IN_SCOPE) === ScopeStatus.IN_SCOPE)
    .map(e => e[0]))
  const tasksByFeature: Record<string, Task[]> = {}
  Object.values(allDocs).filter(d => d.type === DocumentType.TASK && d.parentId && scopedFeatureIds.has(d.parentId))
    .forEach(t => {
      const featureId = t.parentId!
      tasksByFeature[featureId] = [...(tasksByFeature[featureId] || []), documentToTask(t)]
    })
  const featureEstimates: Record<string, TimeEstimate> = {}
  scopedFeatureIds.forEach(id => {
    const tasks = tasksByFeature[id]
    if (!tasks) {
      return
    }
    const minHours = tasks.map(t => t.details?.timeEstimate?.minHours ?? 0).reduce((a, b) => a + b, 0);
    const maxHours = tasks.map(t => t.details?.timeEstimate?.maxHours ?? 0).reduce((a, b) => a + b, 0);
    if (maxHours == 0) {
      return
    }
    featureEstimates[id] = TimeEstimate.create({minHours, maxHours})
  })
  return featureEstimates
}

export function getMinDate(...dates: (Date | undefined)[]): Date | undefined {
  let min: Date | undefined = undefined
  for (const d of dates) {
    if (!d || !min) {
      if (!min) {
        min = d
      }
      continue
    }
    min = d < min ? d : min
  }
  return min
}

type NullableDate = Date | undefined

export function getMaxDate(...dates: NullableDate[]): NullableDate {
  let max: NullableDate = undefined
  for (const d of dates) {
    if (!d || !max) {
      if (!max) {
        max = d
      }
      continue
    }
    max = d > max ? d : max
  }
  return max
}

export interface SortableDocument {
  id: string
  createdAt?: Date
}

export function getOrderedFeatureIds(planIds: string[] | undefined, allFeatures: SortableDocument[]): string[] {
  const sortedDocs = [...allFeatures].sort((a, b) => (a.createdAt?.getTime() || 0) - (b.createdAt?.getTime() || 0))
  if (!planIds || planIds.length === 0) {
    return sortedDocs.map(d => d.id)
  }
  return [...planIds, ...sortedDocs.map(d => d.id).filter(id => !planIds.includes(id))]
}
